import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import shopApi from '@services/shop';

export const useShopInitialisation = (isLoaded, navigate, setStep1Completed, setAddressDetails, setFormData) => {
	const [currentShop, setCurrentShop] = useState(null);
	const [initializing, setInitializing] = useState(true);
	// Multi-shop orgs route under `/:shopId/onboarding`. Without this, the hook
	// would read state from `shops[shops.length - 1]` and pre-fill the wizard
	// with a different location's data — so e.g. switching to a mid-onboarding
	// Manchester would show Bristol's completed steps. Single-shop orgs have
	// no `:shopId` param, so this falls back to the only shop they own.
	const { shopId } = useParams();

	useEffect(() => {
		if (isLoaded) {
			shopApi
				.getMyShops()
				.then((shops) => {
					if (shops && shops.length > 0) {
						// Show whatever shops the user's Clerk org owns. We previously filtered to
						// shops with `plan` starting with "unify", but that excluded test/migration
						// shops still on legacy plans (e.g. `freemium`) — and the single-org-per-user
						// constraint on Unify Clerk already guarantees the user only sees shops
						// from their own org.
						const latestShop = (shopId && shops.find((s) => s.id === shopId)) || shops[shops.length - 1];

						setCurrentShop(latestShop);

						// Mark step 1 as completed if shop has basic details filled in
						const hasBasicDetails = latestShop.name && latestShop.address && latestShop.postcode;

						if (hasBasicDetails) {
							// Split combined address back into addressLine1 and addressLine2
							// Backend stores as: "44 Northumberland St, London"
							const addressParts = latestShop.address ? latestShop.address.split(', ') : [];
							const addressLine1 = addressParts[0] || '';
							const addressLine2 = addressParts.length > 1 ? addressParts.slice(1).join(', ') : '';

							// Parse geolocation if it's a string (backend JSON.stringify's it)
							let geolocation = latestShop.geolocation;
							if (typeof geolocation === 'string') {
								try {
									geolocation = JSON.parse(geolocation);
								} catch (e) {
									geolocation = undefined;
								}
							}

							// ALWAYS populate addressDetails if shop has basic info
							// This fixes Step 1 showing empty after returning from Step 3 OAuth
							setAddressDetails({
								name: latestShop.name,
								addressLine1,
								addressLine2,
								city: latestShop.city,
								postcode: latestShop.postcode,
								country: latestShop.country,
								publicPhoneNumber: latestShop.publicPhoneNumber,
								googlePlaceId: latestShop.googlePlaceId,
								geolocation,
								openingHours: latestShop.openingHours,
								types: latestShop.types
							});

							setFormData((prev) => ({
								...prev,
								name: latestShop.contactName || prev.name,
								email: latestShop.contactEmail || prev.email
							}));

							// If shop has basic details saved, Step 1 is completed
							setStep1Completed(true);
						}
					}
				})
				.catch(() => {
					// Silently handle error
				})
				.finally(() => {
					setInitializing(false);
				});
		}
	}, [isLoaded, shopId, setStep1Completed, setAddressDetails, setFormData]);

	return { currentShop, setCurrentShop, initializing };
};
